import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, CircleNotch, Warning, Bank, LinkSimple } from "@phosphor-icons/react";

/**
 * v3.2.37 · PE Payouts (Stripe Connect Express onboarding). Self-serve —
 * PE clicks "Connect with Stripe" → Stripe-hosted KYC → returns here.
 * Progress persists in `users.stripe_connect_status` and is re-checked
 * from Stripe on every page load so status stays fresh.
 */
export default function PEPayoutsPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(true);
  const [connectBusy, setConnectBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/connect/status");
        setStatus(data);
        if (params.get("return") === "1" && data.status !== "active") {
          toast.info("Onboarding saved. Some steps may still be pending.");
        } else if (params.get("return") === "1" && data.status === "active") {
          toast.success("You're all set — payouts enabled!");
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status check failed");
      } finally { setBusy(false); }
    })();
  }, [params]);

  const startOnboarding = async () => {
    setConnectBusy(true);
    try {
      const { data } = await api.post("/payments/connect/onboard", {
        return_url:  `${window.location.origin}/pe/payouts?return=1`,
        refresh_url: `${window.location.origin}/pe/payouts?refresh=1`,
      });
      window.location.assign(data.url);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start onboarding";
      toast.error(msg);
      setConnectBusy(false);
    }
  };

  const s = status?.status || "not_started";
  const box = (title, desc, cls, icon) => (
    <div className={`mt-6 border-2 p-6 ${cls}`} data-testid={`payouts-state-${s}`}>
      <div className="flex items-start gap-4">
        <div className="shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-black text-white">{title}</div>
          <div className="mt-1 text-sm text-gray-300">{desc}</div>
          {status?.requirements?.length > 0 && (
            <ul className="mt-3 text-xs font-mono text-[#FFCC00] list-disc list-inside">
              {status.requirements.map((r) => (
                <li key={r}>{r.replace(/_/g, " ")}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="payouts" />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// payouts</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="payouts-heading">
          Get paid via Stripe
        </h1>
        <p className="mt-3 text-sm text-gray-400 max-w-xl">
          Connect a Stripe account (takes ~2 minutes) so client milestone
          payments settle directly to your bank. Until connected, your
          earnings sit in the platform ledger and require admin release.
        </p>

        {busy ? (
          <div className="mt-8 text-gray-500 font-mono text-sm">Loading…</div>
        ) : !status ? null : (
          <>
            {s === "active" && box(
              "Payouts active",
              `Your Stripe account is connected and ready. Milestone earnings — minus the ${status.commission_pct}% platform fee — settle automatically to your bank on Stripe's payout schedule.`,
              "border-emerald-400/50 bg-emerald-400/5",
              <CheckCircle size={40} weight="fill" className="text-emerald-400" />
            )}
            {s === "pending" && box(
              "Onboarding partially complete",
              "Stripe still needs a few details before payouts can flow. Continue the onboarding to finish.",
              "border-[#FFCC00]/50 bg-[#FFCC00]/5",
              <CircleNotch size={40} className="text-[#FFCC00] animate-spin" />
            )}
            {s === "not_started" && box(
              "Not connected",
              "You haven't started Stripe onboarding yet. Payouts require a connected Stripe account so we can route your funds directly to your bank.",
              "border-white/20 bg-white/[0.03]",
              <Bank size={40} weight="duotone" className="text-[#00E5FF]" />
            )}
            {s === "disabled" && box(
              "Account paused",
              "Stripe has paused your account. Continue onboarding to resolve the outstanding requirements.",
              "border-[#FF3B30]/50 bg-[#FF3B30]/5",
              <Warning size={40} weight="fill" className="text-[#FF3B30]" />
            )}
            {s === "platform_disabled" && box(
              "Stripe Connect not enabled on this platform",
              "The site admin hasn't activated Stripe Connect yet. Contact support — until then, earnings are tracked in the internal ledger and will be released manually.",
              "border-[#FFCC00]/50 bg-[#FFCC00]/5",
              <Warning size={40} weight="fill" className="text-[#FFCC00]" />
            )}

            <div className="mt-8 flex items-center gap-3">
              {(s === "not_started" || s === "pending" || s === "disabled") && (
                <button onClick={startOnboarding}
                  disabled={connectBusy}
                  className="inline-flex items-center gap-2 btn-primary text-sm py-3 px-5"
                  data-testid="payouts-connect-btn">
                  <LinkSimple size={16} weight="bold" />
                  {connectBusy ? "Loading Stripe…"
                    : s === "not_started" ? "Connect with Stripe"
                    : "Continue onboarding"}
                </button>
              )}
              {s === "active" && (
                <button onClick={() => nav("/payments")}
                  className="btn-primary text-sm py-3 px-5"
                  data-testid="payouts-view-earnings-btn">
                  View earnings →
                </button>
              )}
            </div>

            <div className="mt-10 border border-white/10 bg-white/[0.02] p-5"
                 data-testid="payouts-commission-card">
              <div className="label-tag">// your split</div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-display text-3xl font-black text-[#00E5FF] tabular-nums">
                  {(100 - Number(status.commission_pct || 10)).toLocaleString(undefined, { maximumFractionDigits: 1 })}%
                </div>
                <div className="text-sm font-mono text-gray-400">
                  to you · {Number(status.commission_pct || 10).toLocaleString(undefined, { maximumFractionDigits: 1 })}% platform fee
                </div>
              </div>
              <div className="mt-2 text-xs font-mono text-gray-500">
                Contact admin if you'd like to negotiate a different split.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
